import * as Sentry from "@sentry/react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";

import { StoreFlag } from "./__generated__/useHasFeatureFlag_store.graphql";

function getIsLocalIsh(serverUrl: string) {
  return (
    serverUrl.includes("localhost") ||
    serverUrl.includes("192") ||
    serverUrl.includes("10.0.") ||
    serverUrl.includes("127.0.") ||
    serverUrl.includes("10.101.") ||
    serverUrl.includes("172")
  );
}

export const enum FeatureFlags {
  ICAMinButikIntegration = "ICAMinButikIntegration",
  ActivatePLU = "ActivatePLU",
  ConvertToKPI = "ConvertToKPI",

  Video = "Video",

  Product = "Product",
  ProductDateTracker = "ProductDateTracker",
  EmployeeRole = "EmployeeRole",

  StoresprintUpdate = "StoresprintUpdate",
  SignalWidget = "SignalWidget",
}

const authDomainProd = "https://login.storesprint.app";
const authProd = {
  domain: authDomainProd,

  oauth: {
    clientId: "3AzFJDTvhWRFmLH6rTAxkGuWNobjkxhh",
    issuer: authDomainProd,
    scopes: ["openid", "profile", "email", "offline_access"],
    serviceConfiguration: {
      authorizationEndpoint: `${authDomainProd}/authorize`,
      tokenEndpoint: `${authDomainProd}/oauth/token`,
      registrationEndpoint: `${authDomainProd}/oidc/register`,
      revocationEndpoint: `${authDomainProd}/oauth/revoke`,
    },
  },
};

const authDomainBeta = "https://storesprint-beta.eu.auth0.com";

const authBeta = {
  domain: authDomainBeta,

  oauth: {
    clientId: "bvPgEv4w2gQAztxmU6ZwimHGCfJ9L05i",
    issuer: authDomainBeta,
    scopes: ["openid", "profile", "email", "offline_access"],
    serviceConfiguration: {
      authorizationEndpoint: `${authDomainBeta}/authorize`,
      tokenEndpoint: `${authDomainBeta}/oauth/token`,
      registrationEndpoint: `${authDomainBeta}/oidc/register`,
      revocationEndpoint: `${authDomainBeta}/oauth/revoke`,
    },
  },
};

const baseConfig = {
  Sentry,
  Storage: AsyncStorage,
};

const prodConfig = {
  ...baseConfig,
  mixpanelToken: "b4061606935a71d51f5b9e5cfa925ed1",

  name: "Production",
  auth: authProd,
  baseWSUrl: "wss://api.storesprint.app",
  baseUrl: "https://api.storesprint.app",
  production: true,
  hasFeature: (
    feature: FeatureFlags,
    storeId?: string,
    userId?: string,
    storeFlags?: readonly StoreFlag[]
  ) => {
    if (!storeId) {
      return false;
    }
    switch (feature) {
      case FeatureFlags.Product:
        return !!storeFlags?.includes("Product");
      case FeatureFlags.ProductDateTracker:
        return !!storeFlags?.includes("ProductDateTracker");
      case FeatureFlags.EmployeeRole:
        return !!storeFlags?.includes("EmployeeRole");

      case FeatureFlags.ICAMinButikIntegration:
        return ["2zdi06kf"].includes(storeId); // ICA MAXI Österåker
      case FeatureFlags.ConvertToKPI:
      case FeatureFlags.ActivatePLU:
        return userId === "auth0|5f59e29cacac7f006acf4281";
      case FeatureFlags.Video:
        return (
          userId === "auth0|620d0ece70206600704c441b" ||
          ["bnfw06ip"].includes(storeId)
        );

      case FeatureFlags.StoresprintUpdate:
        return storeFlags?.includes("GroupAccountsAndProfiles") ?? false;
      case FeatureFlags.SignalWidget:
        return storeFlags?.includes("SignalWidget") ?? false;

      default:
        return false;
    }
  },
  adobePDFPreviewClientId: "0f93b69335e14038a075ddae3fa5b349",
  adobePDFPreviewBaseURL: "https://storesprint.app",
  mapboxToken:
    "pk.eyJ1IjoiY2FzcGVyc3RvcmVzcHJpbnQiLCJhIjoiY21saTQ0bGt3MDd0MDNmczh5cXNzeXE0YSJ9.daoiRifBu1BpPbAmsQKJYQ",
};

const getLocalConfig = (customServerUrl?: string) => {
  const serverUrl = customServerUrl || "localhost";
  const isLocalIsh = getIsLocalIsh(serverUrl);
  return {
    ...baseConfig,
    mixpanelToken: isLocalIsh ? undefined : "60d51c08f11683bb730b5981dacda088",
    name: "Local Config",
    auth: authBeta,
    baseWSUrl:
      (isLocalIsh ? "ws://" : "wss://") +
      serverUrl +
      (isLocalIsh ? ":8080" : ""),
    baseUrl:
      (isLocalIsh ? "http://" : "https://") +
      serverUrl +
      (isLocalIsh ? ":8080" : ""),
    production: false,
    hasFeature: (
      feature: FeatureFlags,
      storeId?: string,
      userId?: string,
      storeFlags?: readonly StoreFlag[]
    ) => {
      switch (feature) {
        case FeatureFlags.Product:
          return true;
        case FeatureFlags.ProductDateTracker:
          return !!storeFlags?.includes("ProductDateTracker");
        case FeatureFlags.EmployeeRole:
          return true;

        case FeatureFlags.StoresprintUpdate:
        case FeatureFlags.ICAMinButikIntegration:
          return true;
        case FeatureFlags.SignalWidget:
          return true;
        default:
          return false;
      }
    },
    adobePDFPreviewClientId: "3395b2a0247a4416bc86d505687fb3c5",
    adobePDFPreviewBaseURL: "localhost",
    mapboxToken:
      "pk.eyJ1IjoiY2FzcGVyc3RvcmVzcHJpbnQiLCJhIjoiY2xuY3UzMzdzMDltaTJybzF4OWcyN2hsbSJ9.mMJy9DKb4vLU8HX5AQi2fw",
  };
};

const getBetaConfig = (customServerUrl?: string) => {
  const serverUrl = customServerUrl || "localhost";
  const isLocalIsh = getIsLocalIsh(serverUrl);
  return {
    ...baseConfig,
    mixpanelToken: "60d51c08f11683bb730b5981dacda088",

    name: "Beta",
    auth: authBeta,
    baseWSUrl:
      (isLocalIsh ? "ws://" : "wss://") +
      serverUrl +
      (isLocalIsh ? ":8080" : ""),
    baseUrl:
      (isLocalIsh ? "http://" : "https://") +
      serverUrl +
      (isLocalIsh ? ":8080" : ""),
    production: false,
    hasFeature: (
      feature: FeatureFlags,
      storeId?: string,
      userId?: string,
      storeFlags?: readonly StoreFlag[]
    ) => {
      switch (feature) {
        case FeatureFlags.Product:
          return !!storeFlags?.includes("Product");
        case FeatureFlags.ProductDateTracker:
          return !!storeFlags?.includes("ProductDateTracker");
        case FeatureFlags.EmployeeRole:
          return !!storeFlags?.includes("EmployeeRole");
        case FeatureFlags.ConvertToKPI:
        case FeatureFlags.ActivatePLU:
        case FeatureFlags.Video:
        case FeatureFlags.ICAMinButikIntegration:
          return true;
        case FeatureFlags.SignalWidget:
          return true;
        default:
          return false;
      }
    },
    adobePDFPreviewClientId: "03af0f96e7544819b2fe00ce7a9ecec0",
    adobePDFPreviewBaseURL: "https://beta.storesprint.pages.dev",
    mapboxToken:
      "pk.eyJ1IjoiY2FzcGVyc3RvcmVzcHJpbnQiLCJhIjoiY2xuY3UzMzdzMDltaTJybzF4OWcyN2hsbSJ9.mMJy9DKb4vLU8HX5AQi2fw",
  };
};

const SERVER_CONF = process.env.EXPO_PUBLIC_SERVER_CONF;

const SERVER_URL = process.env.EXPO_PUBLIC_SERVER_URL;

const getConfig = () => {
  if (__DEV__) {
    if (SERVER_CONF === "beta") {
      return getBetaConfig(SERVER_URL);
    }
    return getLocalConfig(SERVER_URL);
  }
  if (SERVER_CONF === "beta") {
    return getBetaConfig(SERVER_URL);
  }

  return prodConfig;
};

export const config = getConfig();

console.log(config.baseUrl);
